import i18next from 'i18next';
import { find, isNumber } from 'lodash';

import {
  FUND_REQUEST_DEFAULT_FEE_AMOUNT,
  FUND_REQUEST_MAX_FEE_AMOUNT,
  FUND_REQUEST_MIN_FEE_AMOUNT,
} from 'constants/funds-requests.constants';
import { FilterDefinitionType, OperationType } from 'enums';
import {
  FilterDefinition,
  FundsRequest,
  FundsRequestFilters,
  P2PProviderSummary,
  Shop,
  User,
} from 'types';
import { formUtils } from 'utils';

const getOperationLabel = (operation: OperationType) => {
  const { t } = i18next;
  const key = `funds_request_operation.${operation}`;
  return i18next.exists(key) ? t(key as any) : key;
};

const getCommonFilters = (
  users?: User[],
  shops?: Shop[],
  p2pProviders?: P2PProviderSummary[],
): FilterDefinition<Partial<FundsRequestFilters>>[] => {
  const { t } = i18next;
  const filters: FilterDefinition<Partial<FundsRequestFilters>>[] = [
    {
      label: t('filters.id'),
      name: 'id',
      type: FilterDefinitionType.Text,
      format: 'uuid',
    },
    {
      label: t('features.funds_request.filters.operation_type'),
      name: 'operationType',
      type: FilterDefinitionType.Enum,
      enum: OperationType,
      getDisplayName: fundsRequestUtils.getOperationLabel,
    },
  ];
  if (users) {
    filters.push({
      label: t('features.funds_request.filters.user'),
      name: 'userId',
      type: FilterDefinitionType.User,
      users,
    });
  }
  if (shops) {
    filters.push({
      label: t('features.funds_request.filters.shop'),
      name: 'shopId',
      type: FilterDefinitionType.Shop,
      shops,
    });
    if (p2pProviders) {
      filters.push({
        label: t('features.funds_request.filters.p2p_provider'),
        name: 'p2pProviderId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(p2pProviders),
        getDisplayName: (id: string) => find(p2pProviders, { id })?.name,
      });
    }
  }
  return filters;
};

const getExportFundsRequestsColumnLabel = (column: string) =>
  i18next.t(`export_funds_requests_column.${column}`, { defaultValue: column });

const getFeeAmountFromConfig = (amount: number, feeConfig: any) => {
  const feeAmount =
    find(
      feeConfig?.intervalFees,
      (interval) => interval?.min <= amount && amount < interval?.max,
    )?.fee || feeConfig?.fee;
  if (!isNumber(feeAmount) || feeAmount < FUND_REQUEST_MIN_FEE_AMOUNT) {
    return FUND_REQUEST_DEFAULT_FEE_AMOUNT;
  }
  if (feeAmount > FUND_REQUEST_MAX_FEE_AMOUNT) {
    return FUND_REQUEST_DEFAULT_FEE_AMOUNT;
  }
  return feeAmount;
};

const isTronSourced = (
  fundsRequest?: Pick<FundsRequest, 'internalWalletDepositTransaction'>,
) => !!fundsRequest?.internalWalletDepositTransaction?.id;

export const fundsRequestUtils = {
  getOperationLabel,
  getCommonFilters,
  getExportFundsRequestsColumnLabel,
  getFeeAmountFromConfig,
  isTronSourced,
};
